import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Loader2 } from "lucide-react";

/** Slice 4 — Live Blast. One blast per sender per hour per Live (server-enforced).
 * Notifies followers + notify-subscribers that the creator is live. */
export default function LiveBlastSheet({ open, onOpenChange, streamId }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/live/${streamId}/blast/status`)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ available: true, retry_in_s: 0 }));
  }, [open, streamId]);

  const send = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/live/${streamId}/blast`);
      toast.success(`Blast sent to ${data.recipients} fans! 📣`);
      setStatus({ available: false, retry_in_s: data.retry_in_s });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send blast.");
      api.get(`/live/${streamId}/blast/status`).then(({ data }) => setStatus(data)).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const mins = status ? Math.ceil(status.retry_in_s / 60) : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="live-blast-sheet"
        className="z-[120] bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 text-white rounded-t-2xl"
      >
        <SheetHeader>
          <SheetTitle className="text-white text-sm uppercase tracking-widest flex items-center gap-2">
            <Megaphone size={14} className="text-yellow-300" /> Live Blast
          </SheetTitle>
        </SheetHeader>
        <div className="mt-3 pb-3">
          <p className="text-sm text-zinc-400 mb-4">
            Notify your followers and subscribers that you're live right now.
            Each sender can blast once per hour per Live.
          </p>
          {status?.available === false ? (
            <div data-testid="blast-cooldown" className="text-center text-sm text-zinc-300 bg-white/5 border border-white/10 rounded-xl py-4">
              ⏳ Blast available again in {mins} min
            </div>
          ) : (
            <button
              type="button"
              data-testid="blast-send-btn"
              onClick={send}
              disabled={busy || !status}
              className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-black font-bold py-3.5 rounded-full text-sm flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <><Megaphone size={16} /> Send Blast</>}
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
